"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RailFlyout = RailFlyout;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const cn_1 = require("../lib/cn");
const overlay_1 = require("../lib/overlay");
const icon_1 = require("./icon");
/** ms of hover before it opens — long enough that sweeping down the rail does not flash eight. */
const OPEN_DELAY = 90;
/** ms it survives the pointer leaving — the bridge across the 6px gap to the flyout. */
const CLOSE_DELAY = 160;
/**
 * ⚠️ **One flyout at a time, across every rail on the page.** Moving from one icon to the next
 * opens the second before the first's close timer runs, and two menus side by side for 160ms reads
 * as a glitch. Module-level for the reason `overlay.ts` keeps its modal stack there.
 */
let closeOpen = null;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ AN ICON RAIL'S ROW, AND WHAT IT OPENS INTO ON HOVER — 14 Oct 2026.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner, after Shopify's admin: *"hovering over any of the icons in the original
 * sidebar brings up a small popup with items in there you can click (where it's applicable)"*. A
 * group's children, a pinned list's entries and its *See more*; an icon with nothing under it gets
 * its name and nothing else.
 *
 * ⚠️⚠️ **PORTALLED, AND THE RAIL IS WHY.** The rail clips each region it draws (`app-shell.tsx`),
 * and a menu hanging off a 52px column is wider than the column — the bell's panel argues the same.
 * `anchorTo` puts it beside the icon and keeps it in the window.
 *
 * ⚠️ **Hover intent both ways.** It opens after a beat and closes after a longer one, so the pointer
 * can cross the gap from the icon to the flyout without either closing under it.
 *
 * ⚠️ **The keyboard reaches it**: focus opens it (but a pointer press does not — the hover already
 * has), Enter on a button and ArrowRight on anything moves into it, the arrows walk its items,
 * ArrowLeft and Escape come back to the icon. It is `useDismiss`, not a trap: Tab leaves.
 */
function RailFlyout({ label, items, emptyHint, more, children, }) {
    const [open, setOpen] = (0, react_1.useState)(false);
    const menuId = (0, react_1.useId)();
    const tooltip = items === undefined && emptyHint === undefined && more === undefined;
    const timer = (0, react_1.useRef)(null);
    const pressed = (0, react_1.useRef)(false);
    /* Set by a keyboard open, run once the surface exists. */
    const focusFirst = (0, react_1.useRef)(false);
    const close = () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
        setOpen(false);
    };
    const closeRef = (0, react_1.useRef)(close);
    closeRef.current = close;
    /* A stable identity for the module-level slot. */
    const [closer] = (0, react_1.useState)(() => () => {
        closeRef.current();
    });
    const show = () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
        if (closeOpen !== null && closeOpen !== closer)
            closeOpen();
        closeOpen = closer;
        setOpen(true);
    };
    /* Declared before `useDismiss` reads it; the ref it reads is filled by then. */
    const triggerRef = (0, react_1.useRef)(null);
    const triggerElement = () => triggerRef.current?.querySelector('a[href], button') ?? null;
    /* ⚠️ Focus handed BACK to the icon must not open the flyout it just closed — `onFocus` reads this
       the way it reads a pointer press. */
    const returnFocus = () => {
        pressed.current = true;
        triggerElement()?.focus();
    };
    const { surface, trigger } = (0, overlay_1.useDismiss)(open && !tooltip, () => {
        /* ⚠️ Focus comes back to the icon when it was inside — an Escape from a menu item otherwise
           drops it on `<body>`, and the next Tab starts from the top of the page. */
        const inside = surface.current?.contains(document.activeElement) === true;
        close();
        if (inside)
            returnFocus();
    });
    const { rect } = (0, overlay_1.useAnchorRect)(open, trigger);
    (0, react_1.useEffect)(() => () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
        if (closeOpen === closer)
            closeOpen = null;
    }, [closer]);
    (0, react_1.useEffect)(() => {
        if (!open) {
            if (closeOpen === closer)
                closeOpen = null;
            return;
        }
        if (!focusFirst.current)
            return;
        focusFirst.current = false;
        surface.current?.querySelector('[role="menuitem"]')?.focus();
    }, [open, closer, surface]);
    /* A tooltip's Escape: WCAG 1.4.13, as `Tooltip` argues. The menu's is `useDismiss`'s. */
    (0, react_1.useEffect)(() => {
        if (!open || !tooltip)
            return;
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                closeRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, tooltip]);
    const later = (fn, ms) => {
        if (timer.current !== null)
            clearTimeout(timer.current);
        timer.current = setTimeout(fn, ms);
    };
    const enter = () => {
        if (open) {
            if (timer.current !== null)
                clearTimeout(timer.current);
            return;
        }
        later(show, OPEN_DELAY);
    };
    const leave = () => {
        later(close, open ? CLOSE_DELAY : 0);
    };
    const openInto = () => {
        focusFirst.current = true;
        show();
        /* Already open: the effect will not run again, so move now. */
        if (open) {
            focusFirst.current = false;
            surface.current?.querySelector('[role="menuitem"]')?.focus();
        }
    };
    const onTriggerKeyDown = (event) => {
        if (tooltip)
            return;
        const target = event.target;
        /* ⚠️ Enter on an ANCHOR is left to the anchor — it is the icon's own destination. */
        const opens = event.key === 'ArrowRight' ||
            ((event.key === 'Enter' || event.key === ' ') && target.tagName === 'BUTTON');
        if (!opens)
            return;
        event.preventDefault();
        openInto();
    };
    const rows = () => [
        ...(surface.current?.querySelectorAll('[role="menuitem"]') ?? []),
    ];
    const onMenuKeyDown = (event) => {
        const all = rows();
        const at = all.indexOf(document.activeElement);
        const go = (index) => {
            event.preventDefault();
            all[(index + all.length) % all.length]?.focus();
        };
        switch (event.key) {
            case 'ArrowDown':
                go(at + 1);
                break;
            case 'ArrowUp':
                go(at <= 0 ? all.length - 1 : at - 1);
                break;
            case 'Home':
                go(0);
                break;
            case 'End':
                go(all.length - 1);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                close();
                returnFocus();
                break;
            case ' ':
                /* A menu item answers Space as well as Enter; an anchor alone answers only Enter. */
                event.preventDefault();
                document.activeElement?.click();
                break;
            default:
                break;
        }
    };
    /** A plain primary click navigates in place; a modified one is the browser's (`spaClick`). */
    const choose = (onSelect) => (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            return;
        event.preventDefault();
        close();
        onSelect();
    };
    const row = (key, item, body, extra, active = false) => {
        const cls = (0, cn_1.cn)('text-foreground hover:bg-accent focus-visible:bg-accent flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-[13px] outline-none transition-colors', active && 'bg-accent font-medium', extra);
        const current = active ? 'page' : undefined;
        return item.href === undefined ? ((0, jsx_runtime_1.jsx)("button", { type: "button", role: "menuitem", tabIndex: -1, "aria-current": current, className: cls, onClick: choose(item.onSelect), children: body }, key)) : ((0, jsx_runtime_1.jsx)("a", { href: item.href, role: "menuitem", tabIndex: -1, "aria-current": current, className: cls, onClick: choose(item.onSelect), children: body }, key));
    };
    const list = items ?? [];
    const flyout = !open || rect === null || typeof document === 'undefined'
        ? null
        : (0, react_dom_1.createPortal)(tooltip ? (
        /* ⚠️ `aria-hidden`: the icon already carries this name, and a tooltip saying it twice
           is the same word announced twice. It is for the eye. */
        (0, jsx_runtime_1.jsx)("div", { "aria-hidden": true, className: "pop-in bg-popover text-popover-foreground shadow-pop pointer-events-none rounded-md border px-2.5 py-1 text-[12px] whitespace-nowrap", style: {
                ...(0, overlay_1.anchorTo)(rect, { side: 'right', align: 'start', width: 0, offset: 8 }),
                width: 'max-content',
                /* Centred on the icon: the tooltip is one 26px line. */
                top: `${String(rect.top + rect.height / 2 - 13)}px`,
            }, children: label })) : ((0, jsx_runtime_1.jsxs)("div", { ref: surface, id: menuId, role: "menu", "aria-label": label, "aria-orientation": "vertical", tabIndex: -1, style: (0, overlay_1.anchorTo)(rect, { side: 'right', align: 'start', width: 224, offset: 6 }), className: "pop-in bg-popover text-popover-foreground shadow-pop flex flex-col overflow-y-auto rounded-md border p-1", onPointerEnter: () => {
                if (timer.current !== null)
                    clearTimeout(timer.current);
            }, onPointerLeave: leave, onKeyDown: onMenuKeyDown, children: [(0, jsx_runtime_1.jsx)("div", { role: "presentation", className: "text-muted-foreground px-2 pt-1 pb-1.5 text-[11px] font-medium", children: label }), list.length === 0 && emptyHint !== undefined ? ((0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground px-2 pb-1.5 text-[11.5px] leading-snug", children: emptyHint })) : null, list.map((item) => row(item.label, item, (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [item.color === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "size-2 shrink-0 rounded-[3px]", style: { background: item.color } })), item.icon === undefined ? null : ((0, jsx_runtime_1.jsx)(icon_1.Icon, { name: item.icon, size: 14, className: "text-muted-foreground" })), (0, jsx_runtime_1.jsx)("span", { className: "min-w-0 flex-1 truncate", children: item.label }), item.marker === undefined ? null : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, className: "bg-primary size-1.5 shrink-0 rounded-full" }), (0, jsx_runtime_1.jsx)("span", { className: "sr-only", children: item.marker })] })), item.count === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11px] tabular-nums", children: item.count }))] }), undefined, item.active === true)), more === undefined
                    ? null
                    : row('more', more, (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { className: "min-w-0 flex-1 truncate", children: more.label }), (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-right", size: 13, className: "opacity-60" })] }), 'text-muted-foreground hover:text-foreground')] })), document.body);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { ref: (node) => {
                    trigger.current = node;
                    triggerRef.current = node;
                }, className: "relative flex shrink-0", ...(tooltip ? {} : { 'aria-haspopup': 'menu', 'aria-expanded': open }), onPointerEnter: enter, onPointerLeave: leave, onPointerDown: () => {
                    pressed.current = true;
                }, onFocus: () => {
                    /* ⚠️ A press focuses too, and the hover has already decided — opening here as well would
                       race the click that follows. Only a keyboard focus opens it. */
                    if (pressed.current) {
                        pressed.current = false;
                        return;
                    }
                    show();
                }, onBlur: (event) => {
                    pressed.current = false;
                    /* Into its own flyout is not leaving it. */
                    const next = event.relatedTarget;
                    if (next !== null && surface.current?.contains(next) === true)
                        return;
                    if (tooltip)
                        close();
                }, onKeyDown: onTriggerKeyDown, onClickCapture: (event) => {
                    /* A button with a menu opens it on a press; it has nowhere else to go. */
                    if (!tooltip && event.target.closest('a[href]') === null)
                        show();
                }, children: children }), flyout] }));
}
//# sourceMappingURL=rail-flyout.js.map